import { mergeAttributes, Node, type Range } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import Suggestion from "@tiptap/suggestion";
import {
    formatVariableLabel,
    parseVariableTokens,
    serialiseVariableToken,
    TEMPLATE_TOKEN_KIND,
    type IVariableToken,
} from "../variable-token";
import { VariableMentionView } from "./variable-mention.view";
import {
    VARIABLE_MENTION_DATA_ATTRIBUTE,
    VARIABLE_MENTION_NODE_NAME,
    VARIABLE_MENTION_SUGGESTION_CHAR,
    VARIABLE_MENTION_SUGGESTION_PLUGIN_KEY,
    VARIABLE_MENTION_TOKEN_ATTRIBUTE,
} from "./variable-mention.constants";
import type { IVariableMentionOptions, IVariableMentionStorage } from "./variable-mention.types";
import {
    canInsertNodeAt,
    isEmptyVariable,
    variableTokenFromNode,
    variableTokenToAttributes,
} from "./variable-mention.utilities";

/**
 * Whether a variable may go in the document: an empty variable (see `isEmptyVariable`), or one that serialises. A
 * template token never may: templates are drawn by the `templateMention` node.
 * @param variable The variable to check.
 * @returns Whether a pill may hold it.
 */
const isValidPillVariable = (variable: IVariableToken): boolean => {
    if (variable.type === TEMPLATE_TOKEN_KIND) {
        return false;
    }

    if (isEmptyVariable(variable)) {
        return true;
    }

    try {
        serialiseVariableToken(variable);

        return true;
    } catch {
        return false;
    }
};

/**
 * Tiptap node for an Ask Spotlight variable: an inline pill that stands for one `IVariableToken`. It renders
 * as a `VariablePill`, serialises to the variable token (`{{type[#subtype]:(key=v[,v…][;…])|title|label[|label…]}}`)
 * and round-trips through HTML on a single `data-token` attribute. The `suggestion` option adds the `@` trigger, and
 * `renderSwap` lets a click on a pill swap its variable. An empty variable (no value picked yet) draws a placeholder
 * pill and serialises to nothing: no token in its plain text, and no `data-token` or text in its HTML.
 */
export const VariableMention = Node.create<IVariableMentionOptions, IVariableMentionStorage>({
    // One indivisible unit inside a line of text: selectable as a whole, never entered or dragged.
    name: VARIABLE_MENTION_NODE_NAME,
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,

    // No bold or italic on a pill. ProseMirror does not enforce this on its own, so the plugin below does.
    marks: "",

    addOptions() {
        return {
            getPillProps: undefined,
            HTMLAttributes: {},
            suggestion: undefined,
            renderSwap: undefined,
        };
    },

    // A swap `openVariableMentionSwap` asked for, until the pill's node view opens it.
    addStorage() {
        return { pendingSwap: null };
    },

    // The five fields of an `IVariableToken`. None is written to or read from HTML on its own: `data-token`
    // carries them all, so a pasted element cannot override one field and keep the rest.
    addAttributes() {
        const tokenOnly = { renderHTML: () => ({}), parseHTML: () => null };

        return {
            type: { default: null, ...tokenOnly },
            subtype: { default: null, ...tokenOnly },
            values: { default: {}, ...tokenOnly },
            title: { default: null, ...tokenOnly },
            labels: { default: [], ...tokenOnly },
        };
    },

    // HTML in: a `<span data-variable-mention data-token="…">` becomes a pill when its token parses as exactly
    // one variable. Anything else is left to the other rules, which treat it as text.
    parseHTML() {
        return [
            {
                tag: `span[${VARIABLE_MENTION_DATA_ATTRIBUTE}]`,
                getAttrs: (element) => {
                    const token =
                        typeof element === "string" ? null : element.getAttribute(VARIABLE_MENTION_TOKEN_ATTRIBUTE);

                    if (token === null) {
                        return false;
                    }

                    const segments = parseVariableTokens(token);

                    if (
                        segments.length !== 1 ||
                        segments[0].type !== "variable" ||
                        !isValidPillVariable(segments[0].variable)
                    ) {
                        return false;
                    }

                    return variableTokenToAttributes(segments[0].variable);
                },
            },
        ];
    },

    // HTML out: the same span, with the pill text as its text. An empty pill is a bare span, which nothing parses back.
    renderHTML({ node, HTMLAttributes }) {
        const variable = variableTokenFromNode(node);
        const attributes = mergeAttributes(
            { [VARIABLE_MENTION_DATA_ATTRIBUTE]: "" },
            this.options.HTMLAttributes,
            HTMLAttributes
        );

        if (isEmptyVariable(variable)) {
            return ["span", attributes];
        }

        return [
            "span",
            { ...attributes, [VARIABLE_MENTION_TOKEN_ATTRIBUTE]: serialiseVariableToken(variable) },
            formatVariableLabel(variable),
        ];
    },

    // Plain text out: `editor.getText()` gives the token, which is what the agent receives. An empty pill gives nothing.
    renderText({ node }) {
        const variable = variableTokenFromNode(node);

        return isEmptyVariable(variable) ? "" : serialiseVariableToken(variable);
    },

    // ProseMirror owns the editor DOM, so a React component draws the pill through a node view.
    addNodeView() {
        return ReactNodeViewRenderer(VariableMentionView);
    },

    addCommands() {
        return {
            insertVariableMention:
                (variable: IVariableToken, range?: Range) =>
                ({ commands, state }) => {
                    // Refuse an invalid variable before it reaches the document, where it would break rendering.
                    if (!isValidPillVariable(variable)) {
                        return false;
                    }

                    // Replace the given range (the `@query`), or else the selection. Reuse a space that already follows.
                    const { from, to } = range ?? state.selection;

                    // A stale range (saved before the document changed) is refused rather than thrown on.
                    if (from < 0 || to > state.doc.content.size || from > to) {
                        return false;
                    }

                    // `insertContent` reports success even where the schema has no room, e.g. inside a code block.
                    // Checked at the target range's `from`, not the (possibly different) selection.
                    if (!canInsertNodeAt(state, state.schema.nodes[this.name], from)) {
                        return false;
                    }

                    const nodeAfter = state.doc.resolve(to).nodeAfter;
                    const followedBySpace = nodeAfter?.isText === true && nodeAfter.text?.startsWith(" ") === true;

                    return commands.insertContentAt({ from, to: followedBySpace ? to + 1 : to }, [
                        { type: this.name, attrs: variableTokenToAttributes(variable) },
                        { type: "text", text: " " },
                    ]);
                },
            replaceVariableMention:
                (position: number, variable: IVariableToken) =>
                ({ tr, dispatch }) => {
                    // Refuse an invalid variable before it reaches the document, where it would break rendering.
                    if (!isValidPillVariable(variable)) {
                        return false;
                    }

                    // `nodeAt` throws outside the document. `tr.doc` rather than `state.doc`, so an earlier command in
                    // the same chain is taken into account.
                    if (position < 0 || position >= tr.doc.content.size) {
                        return false;
                    }

                    if (tr.doc.nodeAt(position)?.type.name !== this.name) {
                        return false;
                    }

                    // One attribute step per field, all in this one transaction: a single undo step. Attribute steps
                    // map no positions, so a `NodeSelection` on the pill survives and the pill stays selected.
                    if (dispatch) {
                        Object.entries(variableTokenToAttributes(variable)).forEach(([name, value]) => {
                            tr.setNodeAttribute(position, name, value);
                        });
                    }

                    return true;
                },
            openVariableMentionSwap:
                (position, options = {}) =>
                ({ tr, dispatch, editor }) => {
                    if (typeof this.options.renderSwap !== "function" || !editor.isEditable) {
                        return false;
                    }

                    // `tr.doc`, so a pill inserted earlier in the same chain is found.
                    if (position < 0 || position >= tr.doc.content.size) {
                        return false;
                    }

                    if (tr.doc.nodeAt(position)?.type.name !== this.name) {
                        return false;
                    }

                    // The node view takes a pending swap when it becomes selected, so a pill that already is would
                    // never take it, and a later selection would open a stale one.
                    if (tr.selection instanceof NodeSelection && tr.selection.from === position) {
                        return false;
                    }

                    // The node view opens the swap once it renders selected: a pill inserted in this transaction has
                    // no node view yet.
                    if (dispatch) {
                        tr.setSelection(NodeSelection.create(tr.doc, position));
                        this.storage.pendingSwap = { ...options, position };
                    }

                    return true;
                },
        };
    },

    // Marks are checked against the parent paragraph, not the pill, so bolding a selection marks the pill too.
    // After each change, take any mark back off every pill. Appended transactions share the user's undo step.
    addProseMirrorPlugins() {
        const nodeName = this.name;
        const { suggestion } = this.options;

        return [
            new Plugin({
                key: new PluginKey(`${nodeName}MarkGuard`),
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some((transaction) => transaction.docChanged)) {
                        return null;
                    }

                    const { tr } = newState;

                    newState.doc.descendants((node, position) => {
                        if (node.type.name !== nodeName) {
                            return;
                        }

                        node.marks.forEach((mark) => tr.removeMark(position, position + node.nodeSize, mark.type));
                    });

                    return tr.steps.length === 0 ? null : tr;
                },
            }),
            // Opt-in: only a consumer that draws a dropdown gets the `@` trigger. `editor`, `pluginKey` and `char`
            // all go after the spread so the options can never point the plugin at another editor or collide its
            // key/trigger with another suggestion (e.g. GAP-1982's `/` suggestion) on the same editor.
            // `allowedPrefixes` keeps Tiptap's default (a space), so an `@` inside a word never opens it.
            // `allow` defaults to "a pill fits here", so `@` in a code block stays text instead of opening a
            // dropdown whose insert would fail; a consumer's own `allow` replaces that default.
            ...(suggestion
                ? [
                      Suggestion({
                          allow: ({ state, range }) => canInsertNodeAt(state, state.schema.nodes[nodeName], range.from),
                          ...suggestion,
                          editor: this.editor,
                          pluginKey: VARIABLE_MENTION_SUGGESTION_PLUGIN_KEY,
                          char: VARIABLE_MENTION_SUGGESTION_CHAR,
                      }),
                  ]
                : []),
        ];
    },

    // Two-step delete: with the caret right after a pill, the first Backspace selects it. The second press
    // falls through to Tiptap's default delete, which keeps the deletion in the history for undo.
    addKeyboardShortcuts() {
        return {
            Backspace: () => {
                const { selection } = this.editor.state;
                const nodeBefore = selection.$from.nodeBefore;

                if (!selection.empty || nodeBefore?.type.name !== this.name) {
                    return false;
                }

                return this.editor.commands.setNodeSelection(selection.$from.pos - nodeBefore.nodeSize);
            },
        };
    },
});
